import { ArrowLeft, FileText, Plus, RotateCw, Sparkles, Trash2, Zap } from "lucide-react";
import { useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useAddClip,
  useDeleteClip,
  useFinalizeWalkthrough,
  useGenerateQuoteFromWalkthrough,
  useQuotesForWalkthrough,
  useRetriggerProcessing,
  useWalkthrough,
} from "@/api/hooks";
import type { WalkthroughClipResponse, WalkthroughResponse } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";
import { formatBytes, formatCents, formatTime, shortId } from "@/lib/format";

export function WalkthroughDetailPage() {
  const { walkthroughId = "" } = useParams<{ walkthroughId: string }>();
  const navigate = useNavigate();
  const wQuery = useWalkthrough(walkthroughId);
  const retrigger = useRetriggerProcessing(walkthroughId);
  const finalize = useFinalizeWalkthrough(walkthroughId);
  const quotesQuery = useQuotesForWalkthrough(walkthroughId);
  const generate = useGenerateQuoteFromWalkthrough();

  if (wQuery.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading walkthrough…</p>;
  }

  if (wQuery.error) {
    return (
      <p className="text-sm text-red-pencil">
        {wQuery.error instanceof ApiError ? wQuery.error.detail : "Load failed."}
      </p>
    );
  }

  const w = wQuery.data;
  if (!w) return null;

  const isCollecting = w.status === "collecting";
  const isProcessing = w.status === "processing";
  const canRetrigger = w.status === "uploaded" || w.status === "processed" || w.status === "failed";
  const headerLabel = isCollecting
    ? `Walkthrough — ${w.clip_count} clip${w.clip_count === 1 ? "" : "s"} so far`
    : w.filename || `Walkthrough · ${shortId(w.id)}`;

  return (
    <div className="space-y-6">
      <Link
        to={`/jobs/${w.job_id}`}
        className="inline-flex items-center gap-1 text-sm text-graphite-soft hover:text-graphite"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        Back to job
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-mono text-2xl font-bold tracking-tight">
            {headerLabel}
            <StatusBadge status={w.status} />
          </h1>
          <p className="mt-1 font-mono text-xs text-graphite-soft tabular-nums">
            {w.clip_count} clip{w.clip_count === 1 ? "" : "s"} · {formatBytes(totalClipBytes(w))} ·{" "}
            {w.uploaded_at ? `first uploaded ${formatTime(w.uploaded_at)}` : "no clips yet"} ·{" "}
            {shortId(w.id)}
            {w.processing_run_id > 0 && ` · run ${w.processing_run_id}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {w.status === "processed" && (
            <button
              type="button"
              onClick={() =>
                generate.mutate(walkthroughId, {
                  onSuccess: (quote) => navigate(`/quotes/${quote.id}`),
                })
              }
              disabled={generate.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              {generate.isPending ? "Generating…" : "Generate quote"}
            </button>
          )}
          {isCollecting && w.clip_count > 0 && (
            <button
              type="button"
              onClick={() => finalize.mutate()}
              disabled={finalize.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <Zap className="h-3.5 w-3.5" aria-hidden />
              {finalize.isPending ? "Finalizing…" : "Done — process this"}
            </button>
          )}
          {!isCollecting && (
            <button
              type="button"
              onClick={() => retrigger.mutate()}
              disabled={!canRetrigger || retrigger.isPending}
              className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
            >
              <RotateCw className="h-3.5 w-3.5" aria-hidden />
              {retrigger.isPending ? "Re-running…" : "Re-run AI"}
            </button>
          )}
        </div>
      </header>

      {finalize.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {finalize.error instanceof ApiError
            ? finalize.error.detail
            : "Could not finalize walkthrough."}
        </p>
      )}

      {generate.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {generate.error instanceof ApiError ? generate.error.detail : "Could not generate quote."}
        </p>
      )}

      {retrigger.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {retrigger.error instanceof ApiError ? retrigger.error.detail : "Could not retrigger."}
        </p>
      )}

      {w.error_message && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {w.error_message}
        </p>
      )}

      <ClipsSection walkthrough={w} disabled={isProcessing} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Stat
          label="First clip SHA-256"
          value={
            w.clips[0]
              ? `${w.clips[0].sha256.slice(0, 16)}…`
              : w.sha256
                ? `${w.sha256.slice(0, 16)}…`
                : "—"
          }
          mono
        />
        <Stat label="Processed at" value={formatTime(w.processed_at)} mono />
      </div>

      <section className="space-y-3">
        <h2 className="font-mono text-lg font-bold tracking-tight">Scope summary</h2>
        {w.scope_summary ? (
          <article className="prose-pencilout rounded-lg border border-rule bg-paper-warm p-5 text-sm leading-relaxed whitespace-pre-wrap">
            {w.scope_summary}
          </article>
        ) : (
          <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
            {w.status === "processing"
              ? "Processing — this usually takes 30–90 seconds. The page polls every 5 seconds."
              : "Not generated yet. Hit retrigger when the upload's complete and Gemini's configured."}
          </p>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-lg font-bold tracking-tight">Quotes</h2>
          <span className="label-mono">{quotesQuery.data?.length ?? 0} total</span>
        </div>
        {quotesQuery.data && quotesQuery.data.length === 0 ? (
          <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
            {w.status === "processed"
              ? 'No quotes yet. Hit "Generate quote" above to draft one from the scope summary.'
              : "Quotes show up here once the walkthrough finishes processing."}
          </p>
        ) : (
          <ul className="space-y-2">
            {quotesQuery.data?.map((q) => (
              <li
                key={q.id}
                className="rounded-lg border border-rule bg-paper-warm transition hover:bg-pencil-yellow-soft"
              >
                <Link
                  to={`/quotes/${q.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-3.5 w-3.5 text-graphite-soft" aria-hidden />
                    <span className="font-mono text-sm">{shortId(q.id)}</span>
                    <span
                      className={[
                        "rounded px-2 py-0.5 font-mono text-xs",
                        q.status === "draft"
                          ? "bg-paper text-graphite-soft"
                          : q.status === "ready"
                            ? "bg-pencil-yellow-soft text-graphite"
                            : q.status === "sent"
                              ? "bg-graphite text-paper"
                              : q.status === "accepted"
                                ? "bg-pencil-yellow text-graphite"
                                : "bg-red-pencil/10 text-red-pencil",
                      ].join(" ")}
                    >
                      {q.status}
                    </span>
                    <span className="text-sm text-graphite-soft">
                      {q.line_items.length} line
                      {q.line_items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <span className="font-mono text-sm font-semibold tabular-nums text-graphite">
                    {formatCents(q.total_cents)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-mono text-lg font-bold tracking-tight">Transcript</h2>
        {w.transcript ? (
          <article className="rounded-lg border border-rule bg-paper-warm p-5 text-sm leading-relaxed text-graphite-soft whitespace-pre-wrap">
            {w.transcript}
          </article>
        ) : (
          <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
            No transcript yet.
          </p>
        )}
      </section>
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  mono?: boolean;
}

function Stat({ label, value, mono = false }: StatProps) {
  return (
    <div className="rounded-lg border border-rule bg-paper-warm px-4 py-3">
      <span className="label-mono">{label}</span>
      <p className={`mt-1 text-base text-graphite ${mono ? "font-mono tabular-nums" : ""}`}>
        {value}
      </p>
    </div>
  );
}

const MAX_CLIPS = 10;
const SOFT_WARN_AT = 5;

function totalClipBytes(w: WalkthroughResponse): number {
  return w.clips.reduce((sum, c) => sum + c.size_bytes, 0);
}

/** Clip list + add-clip + delete-clip flow. Visible on every
 *  walkthrough; "Add clip" is hidden once status is processing/processed
 *  (the current run is locked) but reappears after a re-run completes if
 *  the contractor wants to add more footage. */
function ClipsSection({
  walkthrough,
  disabled,
}: {
  walkthrough: WalkthroughResponse;
  disabled: boolean;
}) {
  const addClip = useAddClip(walkthrough.id);
  const deleteClip = useDeleteClip(walkthrough.id);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    addClip.mutate(file);
    e.target.value = "";
  }

  const atCap = walkthrough.clips.length >= MAX_CLIPS;
  const nearCap = walkthrough.clips.length >= SOFT_WARN_AT;
  const showAddButton = !disabled && walkthrough.status !== "processed";

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-mono text-lg font-bold tracking-tight">Clips</h2>
        {showAddButton && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={atCap || addClip.isPending}
              className="inline-flex items-center gap-1.5 rounded border border-graphite bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-graphite hover:text-paper disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              {addClip.isPending ? "Uploading…" : "Add clip"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleFile}
            />
          </>
        )}
      </div>

      {addClip.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {addClip.error instanceof ApiError ? addClip.error.detail : "Clip upload failed."}
        </p>
      )}

      {atCap && (
        <p className="rounded border border-pencil-yellow/40 bg-pencil-yellow-soft px-3 py-2 text-xs text-graphite">
          {MAX_CLIPS}-clip cap reached. Finalize this walkthrough and create a new one for any
          additional footage.
        </p>
      )}
      {!atCap && nearCap && (
        <p className="rounded border border-rule bg-paper-warm px-3 py-2 text-xs text-graphite-soft">
          {walkthrough.clips.length} of {MAX_CLIPS} clips used. Past 5 clips, AI extraction quality
          dips — consider finalizing this walkthrough and starting a new one for the rest of the
          job.
        </p>
      )}

      {walkthrough.clips.length === 0 ? (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No clips uploaded yet. Click "Add clip" to attach one or more videos before finalizing.
        </p>
      ) : (
        <ol className="space-y-2">
          {walkthrough.clips.map((clip) => (
            <ClipRow
              key={clip.id}
              clip={clip}
              canDelete={!disabled && walkthrough.status !== "processed"}
              onDelete={() => deleteClip.mutate(clip.id)}
              deleting={deleteClip.isPending && deleteClip.variables === clip.id}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

function ClipRow({
  clip,
  canDelete,
  onDelete,
  deleting,
}: {
  clip: WalkthroughClipResponse;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rule bg-paper-warm px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="rounded bg-paper px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-graphite-soft">
          #{clip.clip_order}
        </span>
        <FileText className="h-3.5 w-3.5 text-graphite-soft" aria-hidden />
        <span className="font-mono text-sm">{clip.filename}</span>
        <span className="font-mono text-[11px] tabular-nums text-graphite-faint">
          {formatBytes(clip.size_bytes)} · {clip.content_type}
        </span>
      </div>
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          aria-label={`Remove clip ${clip.clip_order}`}
          className="rounded p-1.5 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </li>
  );
}
